







































































































































































  import { defineComponent, ref, computed, watch, onMounted, onUnmounted, PropType } from 'vue'
  import uuid from 'uuid-random'
  import { Currency, Products, Stocks } from '@one/types'
  import { Cart, Wishlist } from '@one/types/dist/orderpath/app'
  import { useCompareStore } from '~/stores/useCompareStore'
  import useCurrentInstance from '~/utils/useCurrentInstance'
  import { MappedProductStocks } from '~/store/stocks/types'
  import useComposableUtils from '~/composables/useUtils'
  import useCoreUtils from '~/utils/useUtils'
  import Warehouse = Stocks.Warehouse
  import StockVisibilityMode = Stocks.StockVisibilityMode
  import { ProductItem } from '~/plugins/gtm/gtm'
  import OneProductTileRightPrice from '~/components/organisms/product/OneProductTileRightPrice.vue'

  export interface ProductQuantityAndResourceId {
    id?: string
    quantity: number
  }

  export interface WishlistInterface {
    id: string
    name: string
    timestamp: number
  }

  interface WishlistWithQuantityInterface {
    wishlist: WishlistInterface
    quantity: number
  }

  export default defineComponent({
    name: 'OneProductListView',
    components: {
      OneGallery: () => import('~/components/organisms/grid/product/OneGallery.vue'),
      OneEuEnergyLabel: () => import('~/components/organisms/product/OneEuEnergyLabel.vue'),
      OneProductTileGallery: () => import('~/components/organisms/product/OneProductTileGallery.vue'),
      OneProductTileInformation: () => import('~/components/organisms/product/OneProductTileInformation.vue'),
      OneProductTechnicalButton: () => import('~/components/molecules/buttons/OneProductTechnicalButton.vue'),
      OneProductTileRightActions: () => import('~/components/organisms/product/OneProductTileRightActions.vue'),
      OneProductStocksInfo: () => import('~/components/organisms/product/OneProductStocksInfo.vue'),
      OneProductTileRightPrice,
    },
    props: {
      extended: {
        type: Boolean,
        default: false,
      },
      showRightSide: {
        type: Boolean,
        default: true,
      },
      showAddToWishlistButton: {
        type: Boolean,
        default: true,
      },
      showTechTriggerBtn: {
        type: Boolean,
        default: true,
      },
      productId: {
        type: String,
        required: true,
      },
      unit: {
        type: String,
        required: true,
      },
      prices: {
        type: Object as PropType<Products.ProductPrice>,
      },
      priceLoading: {
        type: Boolean,
      },
      stocksLoading: {
        type: Boolean,
      },
      currency: {
        type: Object as PropType<Currency>,
      },
      progress: {
        type: Boolean,
        default: false,
      },
      progressWishlist: {
        type: Boolean,
        default: false,
      },
      carts: {
        type: Array as PropType<Array<Cart.Responses.BasicCartInfo>>,
        required: true,
      },
      wishlists: {
        type: Array as PropType<Array<Wishlist.Responses.BasicWishlist>>,
        required: true,
      },
      loginToBuyLabel: {
        type: String,
        required: true,
      },
    },
    setup(props, { emit }) {
      const vm = useCurrentInstance()
      const { formatProductPreorderDate } = useComposableUtils()
      const { priceTypeLabel } = useCoreUtils(vm)
      const compareStore = useCompareStore()

      const elemId = uuid()
      const showCollapse = ref(false)
      const mainComponentClass = 'one-product-list-view'

      const tileRef = ref<HTMLElement | null>(null)
      const isElementVisible = ref(false)

      const getProduct = computed(() => vm.$store.getters['products/getProduct'])
      const getWarehouse = computed(() => vm.$store.getters['stocks/getWarehouse'])
      const getProductStocks = computed(() => vm.$store.getters['stocks/getProductStocks'])
      const getProductWarehouse = computed(() => vm.$store.getters['stocks/getProductWarehouse'])
      const getProductOrderQuantityInWarehouse = computed(() => vm.$store.getters['stocks/getProductOrderQuantityInWarehouse'])
      const getDefaultWarehouseId = computed(() => vm.$store.getters['stocks/getDefaultWarehouseId'])
      const getCentralWarehouse = computed(() => vm.$store.getters['stocks/getCentralWarehouse'])
      const getProductLabels = computed(() => vm.$store.getters['products/getProductLabels'])
      const getProductImages = computed(() => vm.$store.getters['products/getProductImages'])
      const getCategory = computed(() => vm.$store.getters['categories/getCategory'])
      const theme = computed(() => vm.$store.getters['cms/theme'])
      const productListImgSize = computed(() => vm.$store.getters['cms/productListImgSize'])
      const isCompareEnabled = computed(() => vm.$store.getters['cms/configuration']?.showCompareProducts)
      const getProductPrice = computed(() => vm.$store.getters['products/getProductPrice'] || (() => null))

      const product = computed(() => getProduct.value(props.productId))
      const productH1 = computed(() => product.value?.seo?.h1)

      const shouldBeInDOM = computed(() => isElementVisible.value || props.extended)

      const preorderDate = computed(() => formatProductPreorderDate(product.value?.preorderDate))

      const isProductBlocked = computed(() => getProductLabels.value('Block', props.productId))

      const categoryName = computed(() => {
        const category = getCategory.value(product.value?.canonicalCategory)
        return category ? category.name : null
      })

      const categoryId = computed(() => {
        const category = getCategory.value(product.value?.canonicalCategory)
        return category ? category.id : null
      })

      const stocks = computed<MappedProductStocks | null>(() => getProductStocks.value(props.productId))

      const stockVisibilityModeHidden = computed(() => stocks.value?.stockVisibilityMode === StockVisibilityMode.HIDDEN)

      const currentWarehouse = computed<Warehouse>(() => getWarehouse.value(getDefaultWarehouseId.value))

      const parentWarehouse = computed<Warehouse>(() => currentWarehouse.value && getWarehouse.value(currentWarehouse.value.parent))

      const parentWarehouseId = computed(() => parentWarehouse.value?.id || '')

      const hasSelectedWarehouseParent = computed(() => parentWarehouseId.value !== null && parentWarehouseId.value !== '')

      const firstCentralWarehouse = computed(() => getCentralWarehouse.value && getCentralWarehouse.value[0])

      const parentOrFirstWarehouseId = computed(() => (parentWarehouse.value && parentWarehouse.value.id) || firstCentralWarehouse.value)

      const isSelectedWarehouseCentral = computed(() => currentWarehouse.value?.type?.toString() === 'CENTRAL')

      const isCompared = computed(() => compareStore.isInCompare(props.productId))

      const warehouse = (id: string) => getProductWarehouse.value(props.productId, id)
      const getLabel = (id: string) => warehouse(id) ? warehouse(id).warehouseLabel : '-'
      const getShippingTime = (id: string) => getWarehouse.value(id) && getWarehouse.value(id).shippingTime
      const getAvailability = (id: string) => warehouse(id)?.available

      const labels = computed(() => ({
        firstWarehouse: getLabel(parentWarehouseId.value),
        defaultWarehouse: getLabel(getDefaultWarehouseId.value),
      }))

      const shippingTime = computed(() => ({
        firstWarehouse: getShippingTime(parentWarehouseId.value),
        defaultWarehouse: getShippingTime(getDefaultWarehouseId.value),
      }))

      const warehouses = computed(() => {
        if (!stocks.value) return []

        return stocks.value.warehouses.allIds
          .map((stockId: string) => ({
            id: stockId,
            unity: stocks.value!.unit,
            quantity: getProductOrderQuantityInWarehouse.value(props.productId, stockId),
            label: getLabel(stockId),
            shippingTime: getShippingTime(stockId),
            type: getWarehouse.value(stockId).type,
            available: getAvailability(stockId),
            stockVisibilityModeHidden: stockVisibilityModeHidden.value,
          }))
          .filter((item: any) => {
            if (parentWarehouseId.value) {
              return item.id !== getDefaultWarehouseId.value && item.id !== parentWarehouseId.value
            }
            return item.id !== getDefaultWarehouseId.value
          })
          .sort((a: any, b: any): number => {
            if (a.label < b.label) return -1
            if (a.label > b.label) return 1
            return 0
          })
      })

      const mediumPhotoExtended = (id: string) => {
        return getProductImages.value(id, props.extended ? 'SMALL_300' : productListImgSize.value)
      }

      const onProductClick = (product: any) => {
        const price = getProductPrice.value(product.id)
        vm.$gtm.selectItem({
          item_id: product.id,
          item_name: product.name,
          item_brand: (product.brand && product.brand.name),
          category: vm.$routes.getCategoryPath(product.canonicalCategory) || '',
          price: price?.price || 0,
          discount: price?.catalogPrice - price?.price,
        }, 'one-product-list-view')
      }

      const onAddToCart = ({ id, quantity }: ProductQuantityAndResourceId) => {
        emit('add-to-cart', {
          id,
          productId: product.value.id,
          amountOfProducts: quantity,
        })
      }

      const onAddToWishlist = ({ wishlist, quantity }: WishlistWithQuantityInterface) => {
        emit('add-to-wishlist', {
          wishlist,
          productId: product.value.id,
          amountOfProducts: quantity,
        })
      }

      const onAddToCompare = (product) => {
        compareStore.toggleCompare(product)
      }

      let observer: IntersectionObserver | null = null

      onMounted(() => {
        observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            isElementVisible.value = true
            if (observer && tileRef.value) observer.unobserve(tileRef.value)
          }
        }, {
          rootMargin: '400px 0px'
        })

        if (tileRef.value) observer.observe(tileRef.value)
      })

      onUnmounted(() => {
        if (observer && tileRef.value) observer.unobserve(tileRef.value)
        vm.$plugins.onUnmounted(mainComponentClass)
      })

      watch(isElementVisible, (isVisible) => {
        if (isVisible) {
          vm.$plugins.onMounted(mainComponentClass, vm, {
            productId: props.productId,
          })
        }
      })

      return {
        elemId,
        showCollapse,
        mainComponentClass,
        tileRef,
        shouldBeInDOM,
        product,
        productH1,
        getProductImages,
        mediumPhotoExtended,
        theme,
        preorderDate,
        categoryName,
        categoryId,
        isCompareEnabled,
        stocks,
        warehouses,
        hasSelectedWarehouseParent,
        isSelectedWarehouseCentral,
        isCompared,
        parentWarehouseId,
        shippingTime,
        labels,
        isProductBlocked,
        getDefaultWarehouseId,
        parentOrFirstWarehouseId,
        getProductOrderQuantityInWarehouse,
        priceTypeLabel,
        onProductClick,
        onAddToCart,
        onAddToWishlist,
        onAddToCompare,
      }
    },
  })
